<template>
    <article class="pl-panel pl-panel--raised pl-dossier pl-dossier--oath" :aria-labelledby="nameId">
        <span class="pl-dossier__scene" :style="scene" aria-hidden="true"></span>

        <div class="pl-dossier__art">
            <CharacterPortrait
                :name="displayName"
                :avatar="portrait"
                :alt="t('player.creation.oath.portraitAlt')"
                size="xl"
            />
        </div>

        <div class="pl-dossier__body">
            <div>
                <p class="pl-eyebrow">{{ pathText }}</p>
                <h1 :id="nameId" ref="heading" tabindex="-1" class="pl-title pl-truncate" :title="displayName">
                    {{ displayName }}
                </h1>
            </div>

            <ChipList>
                <li class="pl-chip pl-chip--muted">
                    {{ t('player.creation.oath.age') }}<em>{{ character.bio.age || '—' }}</em>
                </li>
                <li class="pl-chip pl-chip--muted">{{ genderLabel }}</li>
            </ChipList>

            <!-- The ledger. The old summary said "Traits 2" and asked the player to
                 swear an oath over it; a count is not something anyone can check.
                 Every choice is read back by the same name it was picked under. -->
            <dl class="pl-ledger" :aria-label="t('player.creation.oath.ledgerLabel')">
                <div v-for="group in ledger" :key="group.key" class="pl-ledger__row">
                    <dt class="pl-ledger__term">
                        {{ group.label }}<em class="pl-num">{{ group.names.length }}</em>
                    </dt>
                    <dd class="pl-ledger__value">
                        <ul v-if="group.names.length" class="pl-chips">
                            <li v-for="name in group.names" :key="name" class="pl-chip">{{ name }}</li>
                        </ul>
                        <span v-else class="pl-empty__hint">{{ t('player.creation.oath.nothingChosen') }}</span>
                    </dd>
                </div>
            </dl>

            <p v-if="character.bio.background" class="pl-prose pl-clamp-2">
                {{ character.bio.background }}
            </p>
            <p v-else class="pl-empty__hint">{{ t('player.lobby.card.noBiography') }}</p>

            <ul v-if="problems.length" class="pl-prose" role="alert">
                <li v-for="problem in problems" :key="problem" class="pl-field__error">
                    {{ t(`player.creation.oath.missing.${problem}`) }}
                </li>
            </ul>

            <p v-else class="pl-fork__warning">{{ t('player.creation.oath.warning') }}</p>

            <div class="pl-dossier__actions">
                <button
                    type="button"
                    class="pl-btn pl-btn--primary"
                    :aria-busy="submitting"
                    :disabled="submitting || problems.length > 0"
                    @click="$emit('commit')"
                >
                    <span v-if="submitting" class="dx-loader dx-loader--xs" aria-hidden="true"></span>
                    {{ t('player.creation.oath.commit') }}
                </button>
                <button type="button" class="pl-btn pl-btn--ghost" @click="$emit('export')">
                    {{ t('player.creation.oath.export') }}
                </button>
            </div>

            <p v-if="failed" class="pl-field__error" role="alert">
                {{ t('player.creation.oath.failed') }}
            </p>
        </div>

        <img
            v-if="path?.icon"
            :src="path.icon"
            class="pl-dossier__sigil"
            alt=""
            loading="lazy"
            decoding="async"
        />
    </article>
</template>

<script setup>
import {computed, nextTick, onMounted, ref} from 'vue';
import {useI18n} from 'vue-i18n';
import CharacterPortrait from '@/components/Player/CharacterPortrait.vue';
import ChipList from '@/components/Player/ChipList.vue';
import {hasUnusableName} from '@/components/Player/characterPresentation.js';
import {cardTextOf} from '@/components/Player/Creation/cardText.js';
import {flavourOf} from '@/components/Player/Creation/pathFlavour.js';
import {portraitFor} from '@/utils/avatarPresets.js';
import sceneJohn from '@/assets/images/backgrounds/lobby/card-john.webp';
import sceneJson from '@/assets/images/backgrounds/lobby/card-json.webp';

const props = defineProps({
    character: {type: Object, required: true},
    path: {type: Object, default: null},
    /* The catalogues the earlier steps chose from. The character carries only
       uuids, and a uuid is not something a player can check an oath against. */
    modificators: {type: Array, default: () => []},
    schools: {type: Array, default: () => []},
    skills: {type: Array, default: () => []},
    /* The Portrait step's own fetch, filtered to the same (path, gender) this
       Oath is about to submit. Needed only to resolve the chosen id back to an
       image — the character carries the id, not the picture. */
    avatarPresets: {type: Array, default: () => []},
    submitting: {type: Boolean, default: false},
    failed: {type: Boolean, default: false},
});

defineEmits(['commit', 'export']);

const {t, te} = useI18n();
const heading = ref(null);
const nameId = 'pl-oath-name';

const displayName = computed(() => (
    hasUnusableName(props.character.name)
        ? t('player.lobby.card.unfinished')
        : props.character.name
));

const pathText = computed(() => (
    props.path ? cardTextOf(t, te, 'path', props.path).name : t('player.lobby.card.noPath')
));

const genderLabel = computed(
    () => t(`player.creation.identity.genders.${String(props.character.bio.gender).toLowerCase()}`),
);

/* The character does not exist yet, so there is no avatar to fetch. If a
   preset was chosen on the Portrait step, that exact file is already sitting
   in media and is shown directly. Otherwise the picture the server WILL
   install is the placeholder keyed by the two things this screen knows —
   asking for it by that key means the Oath shows the character the player is
   about to meet in the lobby rather than an empty frame, without a second
   copy of six photographs in the bundle. */
const portrait = computed(() => portraitFor(
    props.avatarPresets,
    props.character.bio.avatar_preset,
    props.path,
    props.character.bio.gender,
));

const SCENES = {json: sceneJson, john: sceneJohn};
const scene = computed(() => (
    props.path ? {'--pl-dossier-scene': `url("${SCENES[flavourOf(props.path)]}")`} : {}
));

function namesOf(catalogue, chosen, kind) {
    const byId = new Map(catalogue.map((item) => [item.id, item]));
    return chosen
        .map((id) => byId.get(id))
        .filter(Boolean)
        .map((item) => cardTextOf(t, te, kind, item).name);
}

const ledger = computed(() => [
    {
        key: 'features',
        label: t('player.creation.oath.features'),
        names: namesOf(props.modificators, props.character.modificators, 'modificator'),
    },
    {
        key: 'schools',
        label: t('player.creation.oath.schools'),
        names: namesOf(props.schools, props.character.schools, 'school'),
    },
    {
        key: 'skills',
        label: t('player.creation.oath.skills'),
        names: namesOf(props.skills, props.character.spells, 'skill'),
    },
]);

/* The gate. Every one of these would be rejected by the API — the serializer
   requires all of them — so failing here means failing before the request
   rather than after it, with a line the player can act on instead of a 400. */
const problems = computed(() => {
    const found = [];
    if (hasUnusableName(props.character.name)) found.push('name');
    if (!props.character.path) found.push('path');
    const age = props.character.bio.age;
    if (!Number.isFinite(age) || age < 18 || age > 900) found.push('age');
    return found;
});

onMounted(async () => {
    await nextTick();
    heading.value?.focus();
});
</script>
